import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { NumericField } from "@/components/ui/NumericField";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Shield, Info } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import type { MortgageData } from "../../CalculatorTabs";
import { calculateMortgageData } from "@/utils/calculator/mortgageCalculator";
import { AutoCalculateOptions } from "../../utils/types";
import { stateVAExemptions } from "@/types/vaExemptions";

interface VADisabilityStatusSelectProps {
  mortgageData: MortgageData;
  setMortgageData: React.Dispatch<React.SetStateAction<MortgageData>>;
  autoCalculate: AutoCalculateOptions;
}

const VADisabilityStatusSelect: React.FC<VADisabilityStatusSelectProps> = ({ 
  mortgageData, 
  setMortgageData,
  autoCalculate 
}) => {
  const [showPercentageInput, setShowPercentageInput] = useState(
    mortgageData.vaDisabilityStatus === 'partial'
  );

  const handleDisabilityStatusChange = (value: "none" | "partial" | "full") => {
    console.log('=== VA DISABILITY STATUS CHANGE ===');
    console.log('Previous status:', mortgageData.vaDisabilityStatus);
    console.log('New status:', value);
    
    setShowPercentageInput(value === 'partial');
    
    setMortgageData(prev => {
      const updatedData = {
        ...prev,
        vaDisabilityStatus: value,
        vaDisabilityRatingPercentage: value === 'partial' ? (prev.vaDisabilityRatingPercentage || 50) : null
      };
      
      if (value === 'partial' || value === 'full') {
        console.log('Forcing down payment to zero for disability status:', value);
        updatedData.downPayment = 0;
        updatedData.downPaymentPercentage = 0;
      }
      
      // For 100% disability, also reset property tax to 0
      if (value === 'full') {
        console.log('Forcing property tax to zero for 100% disability');
        updatedData.propertyTax = 0;
      }
      
      const recalculatedData = calculateMortgageData(updatedData);
      
      console.log('Final data after disability status change:', {
        vaDisabilityStatus: recalculatedData.vaDisabilityStatus,
        vaDisabilityRatingPercentage: recalculatedData.vaDisabilityRatingPercentage,
        downPayment: recalculatedData.downPayment,
        downPaymentPercentage: recalculatedData.downPaymentPercentage,
        propertyTax: recalculatedData.propertyTax
      });
      console.log('=== VA DISABILITY STATUS CHANGE END ===');
      
      return recalculatedData;
    });
  };

  const handlePercentageChange = (percentage: number) => {
    setMortgageData(prev => {
      const updatedData = {
        ...prev,
        vaDisabilityRatingPercentage: percentage
      };
      return calculateMortgageData(updatedData);
    });
  };

  const handleVeteranAgeChange = (age: string) => {
    const ageNum = age === '' ? null : parseInt(age, 10);
    setMortgageData(prev => {
      const updatedData = {
        ...prev,
        veteranAge: ageNum
      };
      return calculateMortgageData(updatedData);
    });
  };

  const getStateExemptionPreview = () => {
    if (!mortgageData.vaDisabilityRatingPercentage || !mortgageData.state) return null;
    
    const stateExemption = stateVAExemptions[mortgageData.state];
    if (!stateExemption) return null;
    
    const tier = stateExemption.ratingTiers.find(
      t => mortgageData.vaDisabilityRatingPercentage! >= t.minRating && 
           mortgageData.vaDisabilityRatingPercentage! <= t.maxRating
    );
    
    return tier ? tier.description : null;
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">VA Disability Status</Label>
      
      <Select 
        value={mortgageData.vaDisabilityStatus || ""}
        onValueChange={handleDisabilityStatusChange}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select disability status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No Rating</SelectItem>
          <SelectItem value="partial">10%-90% Rating</SelectItem>
          <SelectItem value="full">100% Rating</SelectItem>
        </SelectContent>
      </Select>
      
      {/* Percentage Input for Partial Disability */}
      <AnimatePresence>
        {showPercentageInput && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="space-y-3 overflow-hidden"
          >
            <div className="flex items-center gap-2">
              <Label className="text-sm">Exact VA Disability Rating (%)</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="w-4 h-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Enter your exact VA disability rating (10-90%)</p>
                    <p className="text-xs mt-1">This affects property tax exemptions in your state</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            
            <div className="flex items-center gap-3">
              <Slider
                value={[mortgageData.vaDisabilityRatingPercentage || 50]}
                onValueChange={(values) => handlePercentageChange(values[0])}
                min={10}
                max={90}
                step={10}
                className="flex-1"
              />
              <NumericField
                value={mortgageData.vaDisabilityRatingPercentage || 50}
                onChange={(val) => handlePercentageChange(val || 50)}
                min={10}
                max={90}
                step={10}
                className="w-20"
                formatAsPercent
                allowDecimals={false}
              />
            </div>
            
            {/* State-specific benefit preview */}
            {getStateExemptionPreview() && (
              <Alert className="bg-primary/10 border-primary/30">
                <Shield className="w-4 h-4" />
                <AlertDescription className="text-sm">
                  <strong>{mortgageData.state} Benefit:</strong> {getStateExemptionPreview()}
                </AlertDescription>
              </Alert>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Veteran Age Input (for states with age-based bonuses like TX) */}
      <AnimatePresence>
        {mortgageData.state === 'TX' && mortgageData.vaDisabilityStatus !== 'none' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="space-y-2 overflow-hidden"
          >
            <div className="flex items-center gap-2">
              <Label className="text-sm">Veteran Age (Optional)</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="w-4 h-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Texas offers additional $12,000 exemption for veterans 65+</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <NumericField
              value={mortgageData.veteranAge || 0}
              onChange={(val) => handleVeteranAgeChange(String(val))}
              min={18}
              max={100}
              placeholder="Enter age (if 65+)"
              className="w-full"
              allowDecimals={false}
            />
            {mortgageData.veteranAge && mortgageData.veteranAge >= 65 && (
              <Alert className="bg-primary/10 border-primary/30">
                <Shield className="w-4 h-4" />
                <AlertDescription className="text-sm">
                  <strong>Age 65+ Bonus:</strong> Additional $12,000 property tax exemption applied!
                </AlertDescription>
              </Alert>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VADisabilityStatusSelect;
